import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { partition } from '@utils/lodash.utils';
import { useWindowDimensions } from '../useWindowDimensions';
import { CustomRef, SectionId, getRefHeight } from '../utils';

const getSectionObserverRootMargin = (windowHeight: number, stickyHeight: number) => {
  const marginTolerance = 2;
  const extraMargin = 24;
  const marginTop = stickyHeight + extraMargin;
  const marginBottom = windowHeight > marginTop ? windowHeight - marginTop - marginTolerance : 0;

  return `-${marginTop}px 0px -${marginBottom}px 0px`;
};

export const useNavigationObserver = (
  stickyRef: MutableRefObject<HTMLDivElement | null>,
  targetRefs: MutableRefObject<CustomRef[]>,
  onIntersect: (newValue: SectionId) => void
) => {
  const { windowHeight } = useWindowDimensions();

  const photosObserver = useRef<IntersectionObserver | null>(null);
  const sectionObserver = useRef<IntersectionObserver | null>(null);

  // To deal with tablet sticky height changing when pricing & cta is shown
  const stickyHeight = getRefHeight(stickyRef);

  const intersectCallback: IntersectionObserverCallback = useCallback(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          onIntersect(entry.target.id as SectionId);
        }
      });
    },
    [onIntersect]
  );

  useEffect(() => {
    if (
      stickyRef.current &&
      targetRefs.current &&
      targetRefs.current?.length > 0 &&
      'IntersectionObserver' in window
    ) {
      const [photosRef, sectionRefs] = partition(targetRefs.current, ['id', SectionId.Photos]);

      photosObserver.current?.disconnect();
      sectionObserver.current?.disconnect();

      photosObserver.current = new IntersectionObserver(intersectCallback, {
        threshold: 1,
        // Root margin to deal with tablet gallery going outside of viewport.
        rootMargin: '0px 500px 0px 500px',
      });

      const rootMargin = getSectionObserverRootMargin(windowHeight, stickyHeight);

      sectionObserver.current = new IntersectionObserver(intersectCallback, {
        rootMargin,
      });

      photosRef.forEach(ref => ref.element && photosObserver.current?.observe(ref.element));
      sectionRefs.forEach(ref => ref.element && sectionObserver.current?.observe(ref.element));
    }

    return () => {
      photosObserver.current?.disconnect();
      sectionObserver.current?.disconnect();
    };
  }, [windowHeight, stickyRef, targetRefs, intersectCallback, stickyHeight]);
};
